// ─────────────────────────────────────────────────────────────────
// Glass-i — Investments API client (Stage 6B.9E).
//
// Thin axios wrappers around /api/erp/accounts/investments/*. The
// Approval-MFA challenge id flows through the standard
// `x-approval-mfa-challenge-id` header on delete / income-post /
// dispose / mature.
// ─────────────────────────────────────────────────────────────────

import client from '../../../../api/client';
import { translateQuery } from './investmentsHelpers';

const BASE = '/api/erp/accounts';
const INV  = `${BASE}/investments`;
const CAT  = `${BASE}/investments/categories`;

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

function mfaHeaders(challengeId) {
  return challengeId ? { 'x-approval-mfa-challenge-id': challengeId } : undefined;
}

export const investmentsApi = {
  // ── Categories ──────────────────────────────────────────────
  listCategories: (query) =>
    client.post(`${CAT}/list`, translateQuery(query) || {}).then(unwrap),
  getCategory: (id) =>
    client.get(`${CAT}/${encodeURIComponent(id)}`).then(unwrap),
  createCategory: (payload) =>
    client.post(CAT, payload).then(unwrap),
  updateCategory: (id, payload) =>
    client.put(`${CAT}/${encodeURIComponent(id)}`, payload).then(unwrap),
  deleteCategory: (id) =>
    client.delete(`${CAT}/${encodeURIComponent(id)}`).then(unwrap),

  // ── Investments ─────────────────────────────────────────────
  list: (query) =>
    client.post(`${INV}/list`, translateQuery(query) || {}).then(unwrap),
  get: (id) =>
    client.get(`${INV}/${encodeURIComponent(id)}`).then(unwrap),
  create: (payload) =>
    client.post(INV, payload).then(unwrap),
  update: (id, payload) =>
    client.put(`${INV}/${encodeURIComponent(id)}`, payload).then(unwrap),
  remove: (id, { approvalMfaChallengeId } = {}) =>
    client.delete(`${INV}/${encodeURIComponent(id)}`,
      { headers: mfaHeaders(approvalMfaChallengeId) }).then(unwrap),

  // ── Transactions (read-only) ────────────────────────────────
  listTransactions: (id) =>
    client.get(`${INV}/${encodeURIComponent(id)}/transactions`).then(unwrap),

  // ── Income ──────────────────────────────────────────────────
  previewIncome: (id, body) =>
    client.post(`${INV}/${encodeURIComponent(id)}/income/preview`,
      body || {}).then(unwrap),
  postIncome: (id, body, { approvalMfaChallengeId } = {}) =>
    client.post(`${INV}/${encodeURIComponent(id)}/income/post`,
      body || {}, { headers: mfaHeaders(approvalMfaChallengeId) }).then(unwrap),

  // ── Disposal ────────────────────────────────────────────────
  dispose: (id, body, { approvalMfaChallengeId } = {}) =>
    client.post(`${INV}/${encodeURIComponent(id)}/dispose`,
      body || {}, { headers: mfaHeaders(approvalMfaChallengeId) }).then(unwrap),

  // ── Maturity ────────────────────────────────────────────────
  mature: (id, body, { approvalMfaChallengeId } = {}) =>
    client.post(`${INV}/${encodeURIComponent(id)}/mature`,
      body || {}, { headers: mfaHeaders(approvalMfaChallengeId) }).then(unwrap),

  // ── Valuation (register-only) ───────────────────────────────
  valuation: (id, body) =>
    client.post(`${INV}/${encodeURIComponent(id)}/valuation`,
      body || {}).then(unwrap),

  // ── Ledger lookups (re-uses existing accounts endpoints) ────
  ledgerLookup: (q, limit = 50) =>
    client.get(`${BASE}/ledgers/lookup`, {
      params: { ...(q ? { q } : {}), limit }
    }).then(unwrap),
  ledgersBySubtype: (subtype, { q, limit = 100 } = {}) =>
    client.get(`${BASE}/ledgers/lookups/by-subtype`, {
      params: { subtype, ...(q ? { q } : {}), limit }
    }).then(unwrap)
};

export default investmentsApi;
